import * as Popover from '@radix-ui/react-popover';
import { addDays, format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Icon, Separator } from '~/components';
import { Button } from '~/components/Button';
import { Checkbox } from '~/components/Checkbox';
import { cn } from '~/lib/cn';

import { chevron, dayPickerClassNames } from './DateRangePicker.styles';

export type PresetOption =
  | 'thisWeek'
  | 'last7Days'
  | 'last8Weeks'
  | 'releaseToDate'
  | 'customDate';
export type DateRangePreset = { label: PresetOption; range: DateRange | null };

const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

interface DateRangePickerProps {
  value: DateRange;
  onApply: (range: DateRange) => void;
  initialReleaseDate: Date;
}

export function DateRangePicker({
  onApply,
  value,
  initialReleaseDate,
}: DateRangePickerProps) {
  const { t } = useTranslation('roster');

  // Date range presets
  const presets: DateRangePreset[] = useMemo(
    () => [
      {
        label: 'thisWeek',
        range: {
          from: addDays(TODAY, -((TODAY.getDay() + 2) % 7)),
          to: TODAY,
        },
      },
      { label: 'last7Days', range: { from: addDays(TODAY, -6), to: TODAY } },
      {
        label: 'last8Weeks',
        range: { from: addDays(TODAY, -7 * 8 + 1), to: TODAY },
      },
      {
        label: 'releaseToDate',
        range: initialReleaseDate
          ? { from: new Date(initialReleaseDate), to: TODAY }
          : null,
      },
      { label: 'customDate', range: null },
    ],
    [initialReleaseDate],
  );

  // States
  const [open, setOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(
    value || presets[0].range,
  );
  const [activePreset, setActivePreset] = useState<PresetOption>(
    presets.length ? presets[0].label : 'thisWeek',
  );

  useEffect(() => {
    const presetMatch = presets.find(
      (p) =>
        p.range &&
        p.range.from &&
        p.range.to &&
        value.from?.getTime() === p.range.from.getTime() &&
        value.to?.getTime() === p.range.to.getTime(),
    );
    setActivePreset(presetMatch?.label || 'customDate');
    setPendingRange(value);
  }, [value, presets]);

  const handlePresetSelect = (preset: DateRangePreset) => {
    if (preset.range) {
      setPendingRange(preset.range);
    } else if (preset.label === 'customDate') {
      setPendingRange(value);
    }
    setActivePreset(preset.label);
  };

  const handleApplyButtonClick = () => {
    if (!pendingRange || !pendingRange.from || !pendingRange.to) {
      return;
    }
    onApply(pendingRange);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          aria-label="date-range-picker-trigger"
          className="relative flex items-center gap-sm rounded-sm border py-sm pl-md pr-2xl transition-colors duration-100 bg-emphasis-default border-emphasis-subtle text-inverse hover:bg-emphasis-strong"
        >
          <span className="text-white">
            <span className="font-semibold">
              {activePreset === 'customDate'
                ? `${t('dateRangePicker.labels.customDate')}: `
                : `${t('dateRangePicker.labels.dateRange')}: `}
            </span>
            {value
              ? t('dateRangePicker.labels.dateRangeBrief', {
                  startDate: value.from
                    ? format(new Date(value.from), 'MMM d, yyyy')
                    : '',
                  endDate: value.to ? format(value.to, 'MMM d, yyyy') : '',
                })
              : ''}
          </span>
          <Icon
            className={cn(chevron({ state: open ? 'open' : 'default' }))}
            size="md"
            type="chevron-down"
          />
        </button>
      </Popover.Trigger>

      <Popover.Content
        side="bottom"
        align="start"
        className="z-10 mt-xs w-[18rem] rounded-sm border bg-level-01 bg-level-03 border-emphasis-subtle"
      >
        <div className="flex flex-col">
          {presets.map((preset) => (
            <div
              role="button"
              aria-label={preset.label}
              aria-pressed={activePreset === preset.label}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePresetSelect(preset);
                }
              }}
              tabIndex={0}
              key={preset.label}
              onClick={(e) => {
                e.preventDefault();
                handlePresetSelect(preset);
              }}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-xs px-md py-[0.75rem] first:rounded-t-sm hover:bg-level-01 focus:outline-none focus:bg-level-01',
                activePreset === preset.label
                  ? 'bg-level-02 text-strong'
                  : 'text-subtle',
              )}
            >
              <div className="flex items-center gap-sm text-body-sm">
                <Checkbox
                  className="tracking-wide"
                  checked={activePreset === preset.label}
                  tabIndex={-1}
                />
                {t(`dateRangePicker.labels.${preset.label}`)}
              </div>
              {preset.label === 'customDate' ? (
                <Icon type="calendar-days" variant="solid" size="sm" />
              ) : null}
            </div>
          ))}
        </div>

        {activePreset === 'customDate' ? (
          <div className="flex">
            <DayPicker
              showOutsideDays
              mode="range"
              captionLayout="dropdown"
              startMonth={initialReleaseDate}
              endMonth={TODAY}
              selected={pendingRange}
              onSelect={setPendingRange}
              numberOfMonths={1}
              classNames={dayPickerClassNames}
              formatters={{
                formatWeekdayName: (weekday) => format(weekday, 'ccccc'),
                formatMonthDropdown: (month) => format(month, 'MMMM'),
              }}
            />
          </div>
        ) : null}

        <Separator />

        <div className="flex justify-end gap-md px-lg py-md font-semibold">
          <Button
            variant="tertiary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('dateRangePicker.labels.cancelButton')}
          </Button>
          <Button className="flex px-md py-sm" onClick={handleApplyButtonClick}>
            {t('dateRangePicker.labels.applyButton')}
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

DateRangePicker.displayName = 'DateRangePicker';
